import React from 'react';

import { Box, Typography } from '@mui/material';

import styled from '@emotion/styled';
import { useModal } from 'mui-modal-provider';

import { VennueAccessRequestItem } from '../../../graphql/API';
import { TikTokOfferRequirement } from '../../../pages/OfferDetail/components/TikTokOfferRequirement';
import {
  fetchAccountBalance,
  handleBalanceIsLoading,
} from '../../../redux/features/balance/balanceSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { delay } from '../../../utils/delay';
import { centsToDollars } from '../../../utils/formatter';
import LoadingButton from '../../Button/LoadingButton';
import { CurrencySign } from '../../CurrencySign/CurrencySign';
import AddBalanceDialog from '../AddBalance/AddBalanceDialog';
import { AddFundsStripeDialog } from '../AddFundsStripe/AddFundsStripeDialog';
import BaseDialog, { BaseDialogProps } from '../Base';

type Props = BaseDialogProps & {
  accessRequest?: VennueAccessRequestItem | null;
  onConfirm?: (accessRequest: VennueAccessRequestItem | null) => void;
};

export const AccessRequestConfirmationIncentiveDialog: React.FC<Props> = ({
  accessRequest,
  onConfirm,
  ...restProps
}) => {
  const { showModal } = useModal();
  const { currency, id: venueID } = useAppSelector((state) => state.venue.value) || {};
  const { value } = useAppSelector((state) => state.balance);
  const data = value || undefined;
  const appDispatch = useAppDispatch();

  const [stripeDialog, setStripeDialog] = React.useState(false);
  const [successAmount, setSuccessAmount] = React.useState(0);
  const [totalAmountWithFee, setTotalAmountWithFee] = React.useState(0);

  const handleStripeDialog = () => {
    setStripeDialog((prev) => !prev);
  };

  const handleAddFundsDialog = () => {
    showModal(AddBalanceDialog, {
      onSubmit: (totalAmountWithFee) => {
        setTotalAmountWithFee(totalAmountWithFee as number);
        handleStripeDialog();
      },
      getOriginalPriceHandle: (amount) => {
        setSuccessAmount(amount as number);
      },
    });
  };

  const venueBalance = centsToDollars(data?.balance || 0);
  const afterPaymentBalance =
    (Number(venueBalance) as number) - (accessRequest?.offer?.incentiveValue || 0);
  const venueBalanceIsLow =
    (accessRequest?.offer?.incentiveValue || 0) > (Number(venueBalance) as number);

  return (
    <BaseDialog
      width='460px'
      height='536px'
      titleComponent={
        <Typography fontSize={'15px'} fontFamily={'Poppins'} color={'#0A0A0A'} fontWeight={600}>
          Confirm request
        </Typography>
      }
      {...restProps}
    >
      <Typography fontSize={'14px'} fontFamily={'Poppins'}>
        {accessRequest?.offer?.title} has an incentive. By accepting the request, this amount will be
        deducted from your wallet.
      </Typography>
      <InfoContainer>
        <InfoItemWithSubtitle>
          <InfoItemRow>
            <InfoItemLabel>Incentive</InfoItemLabel>
            <InfoItemPriceValue>
              {currency && <CurrencySign currency={currency} />}
              {accessRequest?.incentiveValue?.toFixed(2)}
            </InfoItemPriceValue>
          </InfoItemRow>
          {accessRequest?.incentiveValue && accessRequest?.incentiveValue !== accessRequest?.offer?.incentiveValue && (
            <InfoItemSubtitle>
              This user requested your product before you changed the incentive value
            </InfoItemSubtitle>
          )}
        </InfoItemWithSubtitle>
        <InfoItem>
          <InfoItemLabel error={venueBalanceIsLow}>Your balance</InfoItemLabel>
          <InfoItemValue error={venueBalanceIsLow}>
            {currency && <CurrencySign currency={currency} />}
            {venueBalance}
          </InfoItemValue>
        </InfoItem>
        {afterPaymentBalance > 0 && (
          <InfoItem>
            <InfoItemLabel>After the payment</InfoItemLabel>
            <InfoItemValue>
              {currency && <CurrencySign currency={currency} />}
              {afterPaymentBalance.toFixed(2)}
            </InfoItemValue>
          </InfoItem>
        )}
      </InfoContainer>
      {venueBalanceIsLow && (
        <Typography fontSize={'14px'} fontFamily={'Poppins'} color={'#E93F3F'} px={2}>
          You don't have enough balance to accept the request.
        </Typography>
      )}
      <Box mt={3}>
        {accessRequest?.offer?.postOnTikTokVideo && (
          <Box>
            <Typography fontWeight={500} fontFamily={'Poppins'} mb={1} fontSize={'14px'}>
              You'll receive
            </Typography>
            <TikTokOfferRequirement tag={accessRequest?.offer?.tag || ''} />
          </Box>
        )}

        <Box display={'flex'} alignItems={'center'} gap={1.2} mt={5}>
          <LoadingButton
            fullWidth
            variant='outlined'
            color='primary'
            sx={{ border: '1px solid rgba(0, 0, 0, 0.10)', p: 0.8 }}
            onClick={() => {
              restProps.onClose?.({}, 'escapeKeyDown');
            }}
          >
            Cancel
          </LoadingButton>
          {afterPaymentBalance < 0}
          {venueBalanceIsLow ? (
            <LoadingButton
              fullWidth
              variant='contained'
              color='primary'
              sx={{ p: 0.8 }}
              onClick={handleAddFundsDialog}
            >
              + Add funds
            </LoadingButton>
          ) : (
            <LoadingButton
              fullWidth
              variant='contained'
              color='primary'
              sx={{ p: 0.8 }}
              disabled={venueBalanceIsLow}
              onClick={() => {
                if (accessRequest) onConfirm?.(accessRequest);
              }}
            >
              Confirm and Pay ${accessRequest?.offer?.incentiveValue || 0}
            </LoadingButton>
          )}
        </Box>
      </Box>

      <AddFundsStripeDialog
        open={stripeDialog}
        onClose={handleStripeDialog}
        amount={totalAmountWithFee}
        amountWithoutFee={successAmount}
        onSuccess={() => {
          appDispatch(handleBalanceIsLoading());
          delay(10000).then(() => {
            appDispatch(fetchAccountBalance({ venueID: venueID || '' }));
          });
        }}
      />
    </BaseDialog>
  );
};

const InfoContainer = styled(Box)`
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  margin-top: 34px;
`;

const InfoItem = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding: 12px 20px;
`;

const InfoItemLabel = styled(Typography) <{ error?: boolean }>`
  font-size: 14px;
  font-family: Poppins;
  color: ${(props) => (props.error ? '#E93F3F' : '#4a5565')};
`;

const InfoItemValue = styled(Typography) <{ error?: boolean }>`
  font-size: 14px;
  color: ${(props) => (props.error ? '#E93F3F' : '#0a0a0a')};
  font-family: Poppins;
`;

const InfoItemPriceValue = styled(Typography)`
  font-size: 14px;
  color: #37bb90;
  font-family: Poppins;
  font-weight: 600;
`;

const InfoItemWithSubtitle = styled(Box)`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding: 12px 20px;
`;

const InfoItemRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const InfoItemSubtitle = styled(Typography)`
  font-family: 'Poppins', 'Helvetica', 'Arial', sans-serif;
  font-weight: 400;
  font-size: 13px;
  letter-spacing: 0%;
  color: rgba(17, 17, 17, 0.8);
  margin-top: 8px;
`;
